import { useMemo, useState } from "react";
import type { FormState } from "../../../shared/types/workflow/workflow-form.types";
import { useWorkflowFormContext } from "../components/workflow-form-context";

export interface UseWorkflowTransitionProps {
  onSubmit: (data: FormState) => Promise<void>;
  closeImmediately?: boolean;
}

export function useWorkflowTransition(props: UseWorkflowTransitionProps) {
  const [isOpen, setIsOpen] = useState(false);

  const formContext = useWorkflowFormContext();
  const schema = formContext?.schema;

  const hasDialogAction = useMemo(
    () =>
      schema?.action &&
      typeof schema.action === "object" &&
      "type" in schema.action &&
      schema.action.type === "dialog",
    [schema?.action],
  );

  async function handleButtonClick() {
    if (hasDialogAction) {
      setIsOpen(true);
    } else {
      if (!formContext) return;
      formContext.setIsSubmitting(true);
      await props.onSubmit(formContext.formState);
    }
  }

  async function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formContext) return;

    const isValid = await formContext.validateForm();
    if (!isValid) return;
    formContext.setIsSubmitting(true);

    if (props.closeImmediately) {
      props.onSubmit(formContext.formState);
    } else {
      await props.onSubmit(formContext.formState);
    }

    formContext.setIsSubmitting(false);
    formContext.resetForm();
    setIsOpen(false);
  }

  function handleOpenChange(open: boolean) {
    formContext?.resetForm();
    setIsOpen(open);
  }

  return {
    isOpen,
    schema,
    hasDialogAction,
    handleButtonClick,
    handleFormSubmit,
    handleOpenChange,
  };
}
